import { ReactNode, useCallback, useMemo, useState } from "react";
import { invalidateBillQueries } from "@api/bill.api";
import { ScheduleLifecycleRequest } from "@api/scheduleTemplate.api";
import { resolveBillSourceDate } from "@common/helpers/domains/accounting/billScheduleEligibility";
import { selectScheduledBillLines } from "@common/helpers/domains/accounting/scheduleBillRecognitionAccount";
import { Bill } from "@common/types/bill";
import { ScheduleSourceType } from "@common/types/domains/accounting/schedule";
import { useScheduleLifecycleActions } from "@components/accounting/schedules/applications/useScheduleLifecycleActions";
import {
    ScheduleSpecificDateApplyRequest,
    ScheduleSpecificDateApplyResult,
} from "@components/accounting/schedules/manual/scheduleSpecificDateApply";
import { useScheduleSpecificDateApply } from "@components/accounting/schedules/manual/useScheduleSpecificDateApply";
import { buildAccruedBilling } from "@components/accounting/schedules/scheduleApplyAccruedHelpers";
import { SchedulePickerTemplateWithAnchors } from "@components/accounting/schedules/scheduleApplyConfirmHelpers";
import { ScheduleDrawer } from "@components/accounting/schedules/ScheduleDrawer";
import { useInlineScheduleLockedPeriods } from "@components/accounting/schedules/useInlineScheduleLockedPeriods";
import { ShowLockedPeriods } from "@components/accounting/schedules/useScheduleLockedPeriodsDialog";
import { useApplyBillSchedule } from "./useApplyBillSchedule";

export interface BillScheduleConfirmRequest {
    bill: Bill;
    template: SchedulePickerTemplateWithAnchors;
    sourceLineIds?: string[];
    amount: number;
    sourceLabel: string;
}

export interface BillScheduleCallbacks {
    onCreateSchedule: (options: { entityId: number }) => void;
    onScheduleApplied: () => Promise<void>;
    onRequestScheduleConfirm: (
        request: BillScheduleConfirmRequest,
    ) => Promise<boolean>;
    onRequestLifecycleAction: (request: ScheduleLifecycleRequest) => void;
    showLockedPeriods: ShowLockedPeriods;
}

type BillSpecificDateRequest = ScheduleSpecificDateApplyRequest & {
    billId: string;
    sourceLineIds?: string[];
};

function toSpecificDateRequest(
    request: BillScheduleConfirmRequest,
): BillSpecificDateRequest {
    const { bill, template, sourceLineIds } = request;
    const scheduledLines = selectScheduledBillLines({
        lines: bill.lines ?? [],
        sourceLineIds,
    });
    const sourceDate = resolveBillSourceDate(bill);
    return {
        templateId: template.id,
        templateName: template.name,
        entityId: bill.entityId,
        sourceType: ScheduleSourceType.bill,
        amount: request.amount,
        sourceLabel: request.sourceLabel,
        sourceKey: `${bill.id}:${sourceLineIds?.join(",") ?? "bill"}`,
        sourceDate,
        billing: buildAccruedBilling({
            sourceDate,
            amount: request.amount,
            noun: "Bill",
        }),
        sourceLineAccountIds: scheduledLines.map(
            (line) => line.accountId ?? null,
        ),
        billId: bill.id,
        sourceLineIds,
    };
}

/**
 * Page-level schedule wiring for the bill grids and the details panel: the
 * schedule modal a Specific-date or accrued policy opens first, lifecycle
 * modals, the locked periods dialog and the create-policy drawer. Cells and
 * the panel raise requests through table meta or props; every mutation
 * refetches the bill lists, then calls `onRefreshed` so a surface holding a
 * snapshot of a row can pick up the fresh one.
 */
export function useBillScheduleCallbacks(refresh?: {
    onRefreshed?: () => void;
}): {
    scheduleCallbacks: BillScheduleCallbacks;
    scheduleOverlays: ReactNode;
} {
    const onRefreshed = refresh?.onRefreshed;
    const refreshBills = useCallback(async () => {
        await invalidateBillQueries();
        onRefreshed?.();
    }, [onRefreshed]);
    const { showLockedPeriods, lockedPeriodsModal } =
        useInlineScheduleLockedPeriods();
    const { applySchedule, isApplying, linkedConflictModal } =
        useApplyBillSchedule({ showLockedPeriods });
    const applyPending = useCallback(
        (
            pending: BillSpecificDateRequest,
            result: ScheduleSpecificDateApplyResult,
        ) =>
            applySchedule({
                templateId: pending.templateId,
                target: {
                    entityId: pending.entityId,
                    billId: pending.billId,
                    amount: pending.amount,
                    sourceLineIds: pending.sourceLineIds,
                    ...result,
                },
                onApplied: refreshBills,
            }),
        [applySchedule, refreshBills],
    );
    const { open: openSpecificDate, modal: specificDateModal } =
        useScheduleSpecificDateApply<BillSpecificDateRequest>({
            apply: applyPending,
            isApplying,
        });
    const { requestLifecycleAction, modals: lifecycleModals } =
        useScheduleLifecycleActions({
            onCompleted: () => void refreshBills(),
            showLockedPeriods,
        });
    const [creatingForEntityId, setCreatingForEntityId] = useState<
        number | null
    >(null);

    const scheduleCallbacks = useMemo(
        (): BillScheduleCallbacks => ({
            onCreateSchedule: (options) =>
                setCreatingForEntityId(options.entityId),
            onScheduleApplied: refreshBills,
            onRequestScheduleConfirm: (request) =>
                openSpecificDate(toSpecificDateRequest(request)),
            onRequestLifecycleAction: requestLifecycleAction,
            showLockedPeriods,
        }),
        [
            openSpecificDate,
            refreshBills,
            requestLifecycleAction,
            showLockedPeriods,
        ],
    );

    return {
        scheduleCallbacks,
        scheduleOverlays: (
            <>
                {specificDateModal}
                {linkedConflictModal}
                {lifecycleModals}
                {lockedPeriodsModal}
                <ScheduleDrawer
                    open={creatingForEntityId != null}
                    templateId={null}
                    onClose={() => setCreatingForEntityId(null)}
                />
            </>
        ),
    };
}
